import axios from 'axios';
import * as _ from 'lodash';
import { from, Observable } from 'rxjs';
import { map, shareReplay, delay } from 'rxjs/operators';


import {
  IAttachmentInfo,
  IAudit,
  IVerifyAuditProjectsParams,
  IVerifyAuditProjectsResponse,
  ICopyInfo,
  IHandlerPerson,
  IProject,
  ITurnCommand,
  IWorkflow,
  IWorkflowInfo,
  IWorkflowStep,
  IAttachment,
  IProjectLog
} from "src/models";
import { CAN_COPY_WORKFLOW_STEP_NAMES, DELAY_REFRESH_TIME, WORKFLOW_STEP_START_RANK } from "src/consts";

import workflowService from "./workflow.service";
import uiService from "./ui.service";
import projectService from './project.service';
import userService from './user.service';
import { EAuditStatus, EWorkflowType } from 'src/enums';

// 获取当前用户是否可进行抄送
// 下发单、变更单 ||（审批单当前节点可抄送 && 当前用户是当前节点上的审批人）
function getIsCurrentUserCanCopy(workflow: IWorkflow): Promise<boolean> {
  return userService.getCurrentUser().then(currentUser => {
    // - 下发单、变更单（当前节点为开始节点）
    if (WORKFLOW_STEP_START_RANK === workflow.current) {
      return true;
    }

    // - 审批单（当前节点不是开始节点）
    // （审批单当前节点可抄送 && 当前用户是当前节点上的审批人）
    const currentStep: IWorkflowStep = _.find(workflow.steps, { rank: workflow.current });
    let isCurrentUserCanCopy = false;
    const isCurrentStepCanCopy = _.includes(CAN_COPY_WORKFLOW_STEP_NAMES, currentStep.name);  // 审批流当前节点可抄送
    if (isCurrentStepCanCopy) {
      const currentStepPersonUserIds = _.map(currentStep.persons, 'user_id');
      isCurrentUserCanCopy = _.includes(currentStepPersonUserIds, currentUser.user_id);
    }
    return isCurrentUserCanCopy;
  });
}

export default {
  audit$: undefined,
  auditId: undefined,

  /* private */requestAuditObservable(
    id: string,
    enforceNew?: boolean  // 强制获取最新数据，不取缓存
  ): Observable<IAudit> {
    if (enforceNew || !this.audit$ || this.auditId !== id) {
      this.auditId = id;
      this.audit$ = from(  // Observable -> Promise
        axios.get<IAudit>(`/api/audits/${id}`).then((res) => {
          const audit = res.data;
          // audit.data.projects = projectService.unzipProjectsDelayEndOn(audit.data.projects);
          audit.remark = audit.remark || '';  // 处理提交意见为null
          audit.data.modified_reason = audit.data.modified_reason || ''; // 处理变更说明为null
          if (audit.data.receipt_data) {  // 处理回执说明为null
            audit.data.receipt_data.receipt_explain = audit.data.receipt_data.receipt_explain || '';
          }
          if (audit.data.schedule_info) {  // 处理调度说明为null
            audit.data.schedule_info.receipt_remark = audit.data.schedule_info.receipt_remark || '';
          }
          return audit;
        })
      ).pipe(
        shareReplay(1),  // 多播
        map(audit => _.cloneDeep(audit))
      );
    }
    return this.audit$;
  },

  // 获取audit
  getAudit(id: string, enforceNew?: boolean): Promise<IAudit> {
    // 防止id为空（配合服务端调用）
    if (!id) {
      return Promise.resolve(null);
    }
    return this.requestAuditObservable(id, enforceNew).toPromise();
  },

  // (首次)暂存
  firstSaveAudit(audit: IAudit): Promise<any> /* TODO */ {
    // audit.data.projects = projectService.zipProjectsDelayEndOn(audit.data.projects);
    return axios.post<any>(`/api/audits/save`, audit);
  },

  // 暂存
  saveAudit(audit: IAudit): Promise<any> /* TODO */ {
    // audit.data.projects = projectService.zipProjectsDelayEndOn(audit.data.projects);
    return axios.put<any>(`/api/audits/${audit.id}/save`, audit);
  },

  // (首次)提交
  firstSubmitAudit(audit: IAudit): Promise<any> /* TODO */ {
    // audit.data.projects = projectService.zipProjectsDelayEndOn(audit.data.projects);
    audit.data.projects = projectService.zipAuditProjectsSnSync(audit.data.projects);
    return axios.post<any>(`/api/audits/submit`, audit);
  },

  // 提交
  submitAudit(audit: IAudit): Promise<any> /* TODO */ {
    // audit.data.projects = projectService.zipProjectsDelayEndOn(audit.data.projects);
    audit.data.projects = projectService.zipAuditProjectsSnSync(audit.data.projects);
    return axios.put<any>(`/api/audits/${audit.id}/submit`, audit);
  },

  // 审批撤回
  recallAudit(auditId: string): Promise<any> /* TODO */ {
    return this.recallAuditObservable(auditId).toPromise();
  },

  recallAuditObservable(auditId: string): Observable<any> {
    const response$ = from(
        axios.put<any>(`/api/audits/${auditId}/recall`, {})
    ).pipe(delay(DELAY_REFRESH_TIME));
    return response$;
  },

  // 同意审批
  passAudit(audit: IAudit): Promise<any> /* TODO */ {
    return this.passAuditObservable(audit).toPromise();
  },

  passAuditObservable(audit: IAudit): Observable<any> {
    const response$ = from(
        axios.put<any>(`/api/audits/${audit.id}/approve`, audit)
    ).pipe(delay(DELAY_REFRESH_TIME));
    return response$;
  },

  // 审批驳回
  rejectAudit(audit: IAudit): Promise<any> /* TODO */ {
    audit.reject_to_step = audit.reject_to_step || WORKFLOW_STEP_START_RANK;
    return axios.put<any>(`/api/audits/${audit.id}/reject`, audit);
  },

  // 审批废弃
  dropAudit(auditId: string): Promise<any> /* TODO */ {
    return axios.put<any>(`/api/audits/${auditId}/drop`, {});
  },

  // 审批转办
  turnAudit(turnCommand: ITurnCommand): Promise<any> /* TODO */ {
    return this.turnAuditObservable(turnCommand).toPromise();
  },

  turnAuditObservable(turnCommand: ITurnCommand): Observable<any> {
    const response$ = from(
        axios.put<any>(`/api/audits/${turnCommand.id}/transfer`, turnCommand)
    ).pipe(delay(DELAY_REFRESH_TIME));
    return response$;
  },

  // 获取audit的项目列表
  getAuditProjects(auditId: string, enforceNew?: boolean): Promise<IProject[]> {
    return this.getAudit(auditId, enforceNew).then((audit: IAudit) => {
      if (!audit) {
        return [];
      }

      let projects = uiService.assignAuditProjectsSnSync(audit.data.projects);
      projects = uiService.unzipAuditProjectsIsNeedReceiptSync(projects);
      projects = uiService.unzipAuditProjectsIsSecretSync(projects);
      projects = uiService.unzipAuditProjectsEndOnSync(projects);
      return uiService.unzipAuditProjectsAttachmentsSync(projects, audit.attachments.items);
    });
  },

  // 校验audit中的项目（唯一性、不超过5级）
  verifyAuditProjects(params: IVerifyAuditProjectsParams): Promise<IVerifyAuditProjectsResponse> {
    return axios.post<IVerifyAuditProjectsResponse>(`/api/audits/${params.audit_id}/check`, params.projects).then(res => res.data);
  },

  // 获取audit的项目列表信息
  getAuditProjectsInfo(auditId: string, enforceNew?: boolean): Promise<{
    projects: IProject[];
    cached_project_puids: string[];
  }> {
    return this.getAudit(auditId, enforceNew).then((audit: IAudit) => {
      if (!audit) {
        return {
          projects: [],
          cached_project_puids: []
        };
      }

      let projects = uiService.assignAuditProjectsSnSync(audit.data.projects);
      projects = uiService.unzipAuditProjectsIsNeedReceiptSync(projects);
      projects = uiService.unzipAuditProjectsIsSecretSync(projects);
      projects = uiService.unzipAuditProjectsEndOnSync(projects);
      projects = uiService.unzipAuditProjectsAttachmentsSync(projects, audit.attachments.items);
      return {
        projects,
        cached_project_puids: audit.data.cached_project_puids || []
      }
    });
  },

  // 获取audit的附件信息（包括流程附件、项目私有附件）
  getAuditAttachmentInfo(auditId: string, enforceNew?: boolean): Promise<IAttachmentInfo> {
    return this.getAudit(auditId, enforceNew).then((audit: IAudit) => {
      if (!audit) {
        return {
          attachments: { items: [] },
          projectAttachments: { items: [] },
          edit_enable: false
        };
      }

      const attachments = audit.attachments;
      const projectAttachments = { items: [] };

      if (attachments?.items?.length) {
        // 项目的私有附件
        projectAttachments.items = _.filter(attachments.items, (item) => {
          return !!item.puid;
        });
        // 流程附件
        attachments.items = _.filter(attachments.items, (item) => {
          return !item.puid;
        });
      }

      return {
        attachments,
        projectAttachments,
        edit_enable: !audit.is_deleted && !audit.actions?.is_detail
      };
    });
  },

  // 获取audit的抄送信息
  getAuditCopyInfo(auditId: string, enforceNew?: boolean): Promise<ICopyInfo> {
    return this.getAudit(auditId, enforceNew).then(audit => {
      if (!audit) {
        return {
          workflow_copy_info: {
            items: [],
            org_items: []
          },
          edit_enable: true
        };
      }

      const workflow: IWorkflow = audit.flow;
      // - 新建下发单、变更单（无工作流）
      if (!workflow) {
        return {
          workflow_copy_info: audit.data.copy_infos,
          edit_enable: !audit.actions?.is_detail  // 可编辑页面 
        };
      }

      // 当前用户是否可进行抄送
      return getIsCurrentUserCanCopy(workflow).then(isCurrentUserCanCopy => {
        return {
          workflow_copy_info: audit.data.copy_infos,
          edit_enable: !audit.actions?.is_detail && isCurrentUserCanCopy  // 可编辑页面 && 当前用户是否可进行抄送
        };
      });
    });
  },

  // 获取audit的审批历史信息
  getAuditApprovalHistoryInfo(auditId: string): Promise<IProjectLog[]> {
    if (!auditId) {
      return Promise.resolve([]);
    }
    return axios.get<IProjectLog[]>(`/api/audits/${auditId}/logs`).then(res => {
      return res.data;
    });
  },

  // 获取audit的工作流信息
  getAuditWorkflowInfo(
    auditId: string,
    workflowType: EWorkflowType,
    enforceNew?: boolean
  ): Promise<IWorkflowInfo> {
    return this.getAudit(auditId, enforceNew).then((audit: IAudit) => {
      if (!audit) {
        return workflowService.generateWorkflowFromTemplate(workflowType, []).then(workflow => {
          return {
            workflow,
            edit_enable: true
          };
        });
      }

      // 暂存过（audit.flow有值），取暂存的值
      // //  使用工作流模板属性来更新工作流各节点的可编辑属性（1.处理后端同步过来的数据，有的可编辑属性丢失；2.工作流模板更新后，audit中的工作流可编辑属性也做更新）
      if (audit.flow) {
        // bugfix：audit中有值，不需要使用模板属性替换原工作流属性
        // return workflowService.updateWorkflowStepPropertiesFromTemplate(workflowType, audit.flow).then(flow => {
        //   return {
        //     workflow: flow,
        //     edit_enable: !audit.is_deleted && !audit.actions?.is_detail
        //   };
        // });
        return {
          workflow: audit.flow,
          edit_enable: !audit.is_deleted && !audit.actions?.is_detail
        };

        // 未暂存过，通过工作流模板生成工作流
      } else {
        return workflowService.generateWorkflowFromTemplate(workflowType, audit.data?.projects, audit).then(workflow => {
          return {
            workflow,
            edit_enable: !audit.is_deleted
          };
        });
      }
    });
  },

  // 获取audit的工作流当前节点上的审批人
  getAuditWorkflowCurrentStepPersons(auditId: string, enforceNew?: boolean): Promise<IHandlerPerson[]> {
    return this.getAudit(auditId, enforceNew).then((audit: IAudit) => {
      if (!audit?.flow?.steps?.length) {
        return [];
      }
      const steps = audit.flow.steps;
      const current = audit.flow.current;
      const currentStep = _.find(steps, { rank: current });
      return currentStep && currentStep.persons || [];
    });
  },

  // 获取audit的工作流当前节点上的审批人、audit流程状态
  getAuditWorkflowCurrentStepPersonsAndAuditStatus(auditId: string, enforceNew?: boolean): Promise<{ handlePersons: IHandlerPerson[]; auditStatus: EAuditStatus }> {
    return this.getAudit(auditId, enforceNew).then((audit: IAudit) => {
      if (!audit?.flow?.steps?.length) {
        return { handlePersons: [], auditStatus: audit.status };
      }
      const steps = audit.flow.steps;
      const current = audit.flow.current;
      const currentStep = _.find(steps, { rank: current });
      return { handlePersons: currentStep && currentStep.persons || [], auditStatus: audit.status };
    });
  },

  // 获取项目变更单的原始项目列表、实时项目列表
  // - audit.data.projects中暂存的是发生变化的项目savedDirtyProjects，其中，有id的是变更的项目changedProjects，没有id的是追加的项目addedProjects。
  // - 因为暂存时是不存操作权限属性actions的，所以，需要根据changedProjects的id获取项目最新数据freshProjects，将其actions分配给changedProjects
  // - addedProjects的actions全都分配true
  // - 最后
  // - 原始项目列表 originalProjects = freshProjects + 分配了actions的addedProjects
  // - 实时项目列表 projects = 分配了actions的changedProjects + 分配了actions的addedProjects
  getChangeAuditOriginalProjectsAddProjects(auditId: string): Promise<{
    originalProjects: IProject[];
    projects: IProject[];
  }> {
    const changedProjects: IProject[] = []; // 变更的项目列表
    const addedProjects: IProject[] = [];   // 追加的项目列表
    // 获取暂存的项目列表
    return this.getAuditProjects(auditId).then(savedDirtyProjects /* 暂存的项目列表 */ => {
      // 拆分出变更的项目列表、追加的项目列表
      _.forEach(savedDirtyProjects, project => {
        if (projectService.getProjectIsNewSync(project)) {
          addedProjects.push(project);
        } else {
          changedProjects.push(project);
        }
      });

      // 追加的项目列表，actions全都分配true
      _.forEach(addedProjects, project => {
        project.actions = {
          is_can_change: true,
          is_can_add: true,
          is_can_turn_receipt: true
        };
      });

      // 获取变更的项目的最新数据
      const changedProjectIds = _.map(changedProjects, 'id');
      const requests = [];
      _.forEach(changedProjectIds, id => {
        requests.push(projectService.getProject(id));
      });
      return (requests.length ? Promise.all(requests) : Promise.resolve([]));

    }).then((freshProjects: IProject[]) => {
      // 取最新数据，给变更的项目列表分配actions
      _.forEach(freshProjects, freshProject => {
        const changedProject = _.find(changedProjects, { id: freshProject.id });
        changedProject.actions = freshProject.actions;
      });

      // 更新原始项目列表
      let originalProjects = _.concat(freshProjects, addedProjects);
      originalProjects = uiService.assignAuditProjectsSnSync(originalProjects);

      // 更新实时项目列表
      let projects = _.concat(changedProjects, addedProjects);
      projects = uiService.assignAuditProjectsSnSync(projects);

      return {
        originalProjects,
        projects
      };
    });
  },


  // 获取项目变更单的原始项目列表originalProjects、实时项目列表projects
  // - 项目变更单中向用户展示3种项目：未做任何变化的项目cleanProjects（有id），变更的项目changedProjects（有id），追加的项目addedProjects
  // - cached_project_puids存了以上3种项目的puid，用来记录变更单向用户展示的项目列表
  // - 每次
  // - 暂存、提交数据时，audit.data.projects只提交脏项目列表dirtyProjects = changedProjects + addedProjects

  // - 已存在的项目（有id）exitProjects = cleanProjects + changedProjects

  // - 暂存后，再进入项目变更单时，应根据exitProjectPuids（即originalProjectPuids）获取其最新数据originalProjects
  // - 因为暂存时是不存操作权限属性actions的，所以，changedProjects应从originalProjects获取acitions
  // - 而addedProjects的actions全都分配true

  // - 最后
  // - 原始项目列表 originalProjects
  // - 实时项目列表 projects = cleanProjects + 分配了actions的changedProjects + 分配了actions的addedProjects（按cached_project_puids排序）

  getChangeAuditOriginalProjectsAndProjects(auditId: string, enforceNew?: boolean): Promise<{
    originalProjects: IProject[];
    projects: IProject[];
  }> {
    const changedProjects: IProject[] = []; // 变更的项目列表
    const addedProjects: IProject[] = [];   // 追加的项目列表
    let cached_project_puids: string[] = [];  // 项目下发单暂存时的项目列表puids
    // 获取暂存的项目列表
    return this.getAuditProjectsInfo(auditId, enforceNew).then(result => {
      const dirtyProjects = result.projects; // 暂存的项目列表（dirty）
      cached_project_puids = result.cached_project_puids;

      // 拆分出变更的项目列表、追加的项目列表
      _.forEach(dirtyProjects, project => {
        if (projectService.getProjectIsNewSync(project)) {
          addedProjects.push(project);
        } else {
          changedProjects.push(project);
        }
      });

      // 追加的项目列表，actions全都分配true
      _.forEach(addedProjects, project => {
        project.actions = {
          is_can_change: true,
          is_can_add: true,
          is_can_turn_receipt: true
        };
      });

      // 获取原始项目的最新数据
      const addedProjectPuids = _.map(addedProjects, 'puid'); // 追加的项目的puids
      const originalProjectPuids = _.difference(cached_project_puids, addedProjectPuids);  // 已存在的项目的puids
      const requests = [];
      _.forEach(originalProjectPuids, puid => {
        requests.push(projectService.getProjectByPuid(puid));
      });
      return (requests.length ? Promise.all(requests) : Promise.resolve([]));

    }).then((originalProjects: IProject[] /*原始项目列表*/) => {
      // 取最新数据，给变更的项目列表分配actions，flow_status
      _.forEach(changedProjects, changedProject => {
        const originalProject = _.find(originalProjects, { puid: changedProject.puid });
        changedProject.actions = originalProject.actions;
        changedProject.flow_status = originalProject.flow_status;
      });

      // 更新实时项目列表
      let projects: IProject[] = [];
      _.forEach(cached_project_puids, puid => {
        const addedProject = _.find(addedProjects, { puid });
        if (addedProject) {
          projects.push(addedProject);
          return;
        }
        const changedProject = _.find(changedProjects, { puid });
        if (changedProject) {
          projects.push(changedProject);
          return;
        }
        const originalProject = _.find(originalProjects, { puid });
        if (originalProject) {
          projects.push(originalProject);
          return;
        }
      });
      projects = uiService.assignAuditProjectsSnSync(projects);

      return {
        originalProjects,
        projects
      };
    });
  },

  // 新建项目变更单，通过选择的项目puids，获取项目列表数据
  getProjectChangeNewProjects(puids: string[]): Promise<IProject[]> {
    if (!puids) {
      return Promise.resolve([]);
    }
    const requests = [];
    _.forEach(puids, (puid) => {
      requests.push(projectService.getProjectByPuid(puid));
    });
    return Promise.all(requests).then(projects => {
      uiService.assignProjectsSnSync(projects);
      return projects;
    });
  },

  // （系统管理员）更新流程附件
  // （因为该 api 会直接覆盖所有附件，所以 attachments 中应带上原项目私有附件，防止误删）
  updateAttachments(auditId: string, attachments: IAttachment[]): Promise<any> {
    const data = {
      attachments: {
        items: attachments
      }
    };
    return axios.put<any>(`/api/audits/${auditId}/update-attachments`, data);
  },

  // 创建项目变更单
  createUpdateProjectsAudit(puids: string[]): Promise<string> /* auditId */ {
    return axios.get<string>(`/works/projects/new-change`, {
      params: { puids }
    }).then(res => {
      const url = res.data; // 变更单地址
      return url.split('/').pop();
    });
  },

  // 跳转到空项目变更单（带着选中项目的 puids）
  toProjectChangeNew(puids: string[]) {
    if (!puids) {
      return;
    }
    const puidParamStrings = [];
    _.forEach(puids, (puid) => {
      puidParamStrings.push(`puid=${puid}`);
    });
    window.location.href = `/works/project-change-new?${puidParamStrings.join('&')}`;
  }
};
